import { useMemo, useState } from 'react';
import { useQuery, useQueries, useQueryClient } from 'react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import api, { getOpenDefects } from '../../../lib/api';
import { useAuthStore } from '../../../store/authStore';
import { parseDateSafe } from '../../../lib/dateUtils';
import { ELEMENT_TYPE_OPTIONS, sortSessionsByElementTypeOrder } from '../../../constants/AssetTestConfig';
import { isOwnInProgressSession } from '../../../lib/sessionOwnership';
import { useSitesFormActions } from './useSitesFormActions';
import { useSitesHoverPreview } from './useSitesHoverPreview';

function isAdmin(user) {
  return (user?.role || '').toLowerCase() === 'admin';
}

/**
 * Page-level data hook for Sites.jsx. Owns all queries, mutations, derived
 * memos, hover-preview machinery, and form/modal handlers. The page component
 * calls this once and receives everything it needs to render.
 */
export function useSites() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManageSites = isAdmin(user);
  const isManageSitesContext = location.pathname.startsWith('/settings/sites');

  const [showRepairsModal, setShowRepairsModal] = useState(false);
  const [selectedRepairSite, setSelectedRepairSite] = useState(null);
  const [siteSortBy, setSiteSortBy] = useState('next_test_due_date');
  const [siteSortOrder, setSiteSortOrder] = useState('asc');
  const [siteFilter, setSiteFilter] = useState('all');
  const [sitePage, setSitePage] = useState(1);

  const formActions = useSitesFormActions(queryClient);

  const { data: sitesResponse, isLoading, error } = useQuery(
    ['sites', siteSortBy, siteSortOrder, sitePage],
    () => api.get('/sites/with-counts', { params: { sortBy: siteSortBy, sortOrder: siteSortOrder, page: sitePage, limit: 50 } }).then((res) => res.data)
  );
  const sites = sitesResponse?.sites ?? null;
  const sitePagination = sitesResponse?.pagination ?? null;

  const assetsTotalCountQuery = useQuery(
    ['assetsCount', 'all'],
    () => api.get('/assets', { params: { page: 1, limit: 1 } }).then((res) => res.data),
    { staleTime: 60 * 1000 }
  );

  const assetsByElementTypeQueries = useQueries(
    ELEMENT_TYPE_OPTIONS.map((opt) => ({
      queryKey: ['assetsCount', 'elementType', opt.value],
      queryFn: () => api.get('/assets', { params: { page: 1, limit: 1, elementType: opt.value } }).then((res) => res.data),
      staleTime: 60 * 1000,
    }))
  );

  const assetsSummary = useMemo(() => {
    const total = Number(assetsTotalCountQuery.data?.pagination?.totalCount ?? 0);
    const isLoadingSummary = assetsTotalCountQuery.isLoading || assetsByElementTypeQueries.some((q) => q.isLoading);
    const isError = Boolean(assetsTotalCountQuery.error) || assetsByElementTypeQueries.some((q) => q.error);
    const breakdown = ELEMENT_TYPE_OPTIONS.map((opt, i) => ({
      key: opt.value,
      label: opt.label,
      count: Number(assetsByElementTypeQueries[i]?.data?.pagination?.totalCount ?? 0),
    }));
    return { total, breakdown, isLoading: isLoadingSummary, isError };
  }, [assetsTotalCountQuery.data, assetsTotalCountQuery.isLoading, assetsTotalCountQuery.error, assetsByElementTypeQueries]);

  const defectCountBySiteId = useMemo(() => {
    const m = new Map();
    (sites || []).forEach((site) => {
      m.set(String(site.id), {
        count: Number(site.open_defect_count ?? 0),
        isLoading: false,
        isError: false,
      });
    });
    return m;
  }, [sites]);

  const monthRange = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const endExclusive = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return { start, endExclusive };
  }, []);

  const filteredSites = useMemo(() => {
    const all = sites || [];
    if (siteFilter === 'due_this_month') {
      return all.filter((s) => {
        const d = parseDateSafe(s.next_test_due_date);
        if (!d) return false;
        return d >= monthRange.start && d < monthRange.endExclusive;
      });
    }
    if (siteFilter === 'repairs_due') {
      return all.filter((s) => {
        const meta = defectCountBySiteId.get(String(s.id));
        return Boolean(meta) && !meta.isLoading && !meta.isError && (meta.count ?? 0) > 0;
      });
    }
    return all;
  }, [sites, siteFilter, monthRange, defectCountBySiteId]);

  const dueThisMonthIds = useMemo(() => {
    const ids = new Set();
    (filteredSites || []).forEach((s) => {
      const d = parseDateSafe(s.next_test_due_date);
      if (d && d >= monthRange.start && d < monthRange.endExclusive) ids.add(s.id);
    });
    return ids;
  }, [filteredSites, monthRange]);

  const showUpcomingDivider =
    siteFilter === 'all' &&
    dueThisMonthIds.size > 0 &&
    (filteredSites || []).some((s) => !dueThisMonthIds.has(s.id));

  const summaryCounts = useMemo(() => {
    const total = (sites || []).length;
    if (total === 0) return { total: 0, compliant: 0, repairsDue: 0, isLoading: false, isError: false };
    const rows = (sites || []).map((s) => defectCountBySiteId.get(String(s.id)) || { count: 0, isLoading: true, isError: false });
    const isLoadingAny = rows.some((r) => r.isLoading);
    const isErrorAny = rows.some((r) => r.isError);
    if (isLoadingAny) return { total, compliant: null, repairsDue: null, isLoading: true, isError: isErrorAny };
    const repairsDue = rows.filter((r) => r.count > 0).length;
    const compliant = total - repairsDue;
    return { total, compliant, repairsDue, isLoading: false, isError: isErrorAny };
  }, [sites, defectCountBySiteId]);

  const userId = user?.id ?? user?.userId;
  const { data: inProgressRaw, isError: inProgressError, error: inProgressErrorDetail } = useQuery(
    ['sessions-in-progress', userId],
    () => api.get('/sessions/in-progress').then((res) => res.data),
    { staleTime: 0, enabled: !!userId }
  );

  const inProgressSessions = inProgressError ? [] : (inProgressRaw ?? []);
  const inProgressWarning = Boolean(inProgressErrorDetail);

  const inProgressBySite = useMemo(() => {
    const m = new Map();
    (inProgressSessions || []).forEach((s) => {
      const sid = s.site_id != null ? Number(s.site_id) : NaN;
      if (!Number.isNaN(sid)) {
        if (!m.has(sid)) m.set(sid, []);
        m.get(sid).push(s);
      }
    });
    // Own sessions first (sort is stable, so element-type order survives within each group): a site
    // row stacks every session it has, and resume buttons sitting above the "Started by X" labels
    // stops a colleague's label reading as the caption for your button.
    m.forEach((arr, key) => {
      const rows = sortSessionsByElementTypeOrder(arr);
      m.set(key, rows.sort(
        (a, b) => Number(isOwnInProgressSession(b, user)) - Number(isOwnInProgressSession(a, user))
      ));
    });
    return m;
  }, [inProgressSessions, user]);

  const selectedRepairSiteId = selectedRepairSite?.id ? String(selectedRepairSite.id) : '';
  const {
    data: selectedSiteRepairsPayload,
    isLoading: selectedSiteRepairsLoading,
    error: selectedSiteRepairsError,
  } = useQuery(
    ['siteOpenDefectsDetail', selectedRepairSiteId],
    () => getOpenDefects({ siteId: selectedRepairSiteId, page: 1, limit: 100 }),
    {
      enabled: showRepairsModal && !!selectedRepairSiteId,
    }
  );

  const hoverPreview = useSitesHoverPreview(sites, defectCountBySiteId, inProgressBySite);

  const openOutstandingRepairs = (siteId) => {
    const site = sites?.find((s) => String(s.id) === String(siteId));
    if (!site) return;
    setSelectedRepairSite(site);
    setShowRepairsModal(true);
  };

  const closeOutstandingRepairs = () => {
    setShowRepairsModal(false);
    setSelectedRepairSite(null);
  };

  const handleStartRepair = (siteId) => {
    navigate('/repairs', { state: { prefillSiteId: String(siteId) } });
    closeOutstandingRepairs();
  };

  return {
    navigate,
    isManageSitesContext,
    canManageSites,
    sites,
    sitePagination,
    isLoading,
    error,
    siteFilter,
    setSiteFilter,
    siteSortBy,
    setSiteSortBy,
    siteSortOrder,
    setSiteSortOrder,
    sitePage,
    setSitePage,
    filteredSites,
    dueThisMonthIds,
    showUpcomingDivider,
    summaryCounts,
    assetsSummary,
    defectCountBySiteId,
    inProgressBySite,
    inProgressWarning,
    ...hoverPreview,
    ...formActions,
    showRepairsModal,
    selectedRepairSite,
    selectedSiteRepairsPayload,
    selectedSiteRepairsLoading,
    selectedSiteRepairsError,
    openOutstandingRepairs,
    closeOutstandingRepairs,
    handleStartRepair,
  };
}
